import { DateTimePicker, type DateTimePickerProps } from "@msys/ui";
import {
  FormControl,
  FormHelperText,
  type TextFieldProps,
} from "@mui/material";
import { useFormikContext } from "formik";
import { get, isString } from "lodash-es";
import { Moment } from "moment";
import { type ChangeEvent } from "react";
import { browserHasInputDateTime } from "../../featureDetection.js";

export interface Props extends Omit<DateTimePickerProps, "value" | "onChange"> {
  id?: string;
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
  name: string;
  value?: Moment | null;
  placeholder?: string;
  onChange?: (
    e: ChangeEvent<HTMLInputElement> | null,
    newValue: Moment | null
  ) => void | Promise<void>;
  textFieldProps?: Omit<TextFieldProps, "value" | "defaultValue" | "onChange">;
}

export const DateTimePickerField = ({
  fullWidth = true,
  name,
  value,
  onChange,
  textFieldProps,
  placeholder,
  ...otherProps
}: Props) => {
  const { values, setFieldValue, errors, isSubmitting } =
    useFormikContext<Record<string, any>>();

  const error = get(errors, name);

  return (
    <FormControl
      fullWidth={fullWidth}
      required={otherProps.required}
      error={Boolean(error)}
      disabled={otherProps.disabled}
    >
      <DateTimePicker
        browserHasInputDateTime={browserHasInputDateTime}
        value={value ?? get(values, name) ?? null}
        onChange={
          onChange ??
          (async (e, date) => {
            if (otherProps.isClearable || (date && date.isValid())) {
              await setFieldValue(
                name,
                date ? (date.isValid() ? date : null) : null
              );
            }
          })
        }
        textFieldProps={{
          name,
          placeholder,
          error: Boolean(error),
          disabled: otherProps.disabled ?? isSubmitting,
          ...textFieldProps,
        }}
        {...otherProps}
        disabled={otherProps.disabled ?? isSubmitting}
      />
      {Boolean(error) && isString(error) && (
        <FormHelperText>{error}</FormHelperText>
      )}
    </FormControl>
  );
};
